import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  LogOut, Plus, RefreshCw, Settings as SettingsIcon, Pencil, Trash2,
  Save, ExternalLink, CheckCircle2, AlertCircle, Flame, Link2, Download, Bell, Copy,
  Eye, EyeOff, X, Send, Image, Sparkles,
} from "lucide-react";
import Logo from "../components/Logo";
import StatsTab from "../components/StatsTab";
import MessagesTab from "../components/MessagesTab";
import BlogAdminTab from "../components/BlogAdminTab";
import EbayAdminTab from "../components/EbayAdminTab";
import NewsletterAdminTab from "../components/NewsletterAdminTab";
import { api, fileUrl, STORE_LABEL, priceFmt, dealTitle } from "../lib/api";

const CATEGORIES_FALLBACK = [
  "star-wars","technic","icons","creator","city","architecture","harry-potter",
  "marvel","dc","ninjago","friends","speed-champions","disney","ideas","minecraft",
  "super-mario","animal-crossing","botanical","dreamzzz","duplo","brickheadz",
  "sonic","avatar","wicked","one-piece","minifigures","altro"
];
const STORES = ["amazon","aliexpress","ebay","altro"];

const emptyDeal = {
  title: "", title_display: "", description: "", analisi_breve: "", image_url: "", affiliate_url: "",
  store: "amazon", category: "altro",
  price_current: "", price_original: "", discount_percent: "",
  hot: false, published: true, expires_at: "",
};

export default function AdminDashboard() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState("deals");
  const { data: catData } = useQuery({
    queryKey: ["admin-categories"],
    queryFn: () => api.get("/categories").then((r) => r.data),
    staleTime: 5 * 60_000,
  });
  const officialCats = catData && catData.length
    ? catData
    : CATEGORIES_FALLBACK.map((k) => ({ key: k, label: k }));
  const [settingsDraft, setSettingsDraft] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [generatingAnalisi, setGeneratingAnalisi] = useState(false);
  const [analisiMsg, setAnalisiMsg] = useState("");
  const [batchAnalisi, setBatchAnalisi] = useState(null); // {running, total, done, ok, failed, current_title, ...}
  const [batchContent, setBatchContent] = useState(null); // full-editorial batch state
  const [batchArticles, setBatchArticles] = useState(null); // blog batch state
  const [batchCategories, setBatchCategories] = useState(null); // category intros batch state

  const hasToken = typeof window !== "undefined" && !!localStorage.getItem("bdd_token");

  useEffect(() => {
    if (!hasToken) nav("/admin/login");
  }, [hasToken, nav]);

  const dealsQuery = useQuery({
    queryKey: ["admin-deals"],
    enabled: hasToken,
    queryFn: () =>
      api.get("/admin/deals", { params: { limit: 200 } }).then((r) => r.data.items),
    retry: (_count, err) => err?.response?.status !== 401,
  });

  useEffect(() => {
    if (dealsQuery.error?.response?.status === 401) {
      localStorage.removeItem("bdd_token");
      nav("/admin/login");
    }
  }, [dealsQuery.error, nav]);

  const settingsQuery = useQuery({
    queryKey: ["admin-settings"],
    enabled: hasToken,
    queryFn: () => api.get("/admin/settings").then((r) => r.data),
  });

  // Use draft if user has edited, otherwise show fetched values
  const settings = settingsDraft || settingsQuery.data;
  const updateSettings = (patch) =>
    setSettingsDraft({ ...(settings || {}), ...patch });

  const allDeals = dealsQuery.data || [];
  const loading = dealsQuery.isLoading;

  // Source filter (manuale | telegram | ebay_api | all)
  const [sourceFilter, setSourceFilter] = useState("all");
  const deals = sourceFilter === "all"
    ? allDeals
    : allDeals.filter((d) => (d.source || "manual") === sourceFilter);
  const sourceCounts = allDeals.reduce((acc, d) => {
    const s = d.source || "manual";
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});

  const reloadDeals = () => qc.invalidateQueries({ queryKey: ["admin-deals"] });
  const reloadSettings = () => {
    setSettingsDraft(null);
    qc.invalidateQueries({ queryKey: ["admin-settings"] });
  };

  const logout = () => {
    localStorage.removeItem("bdd_token");
    localStorage.removeItem("bdd_user");
    nav("/admin/login");
  };

  const openCreate = () => {
    setEditing({ ...emptyDeal });
    setAutoFillMsg(null);
    setShowForm(true);
  };
  const openEdit = (d) => {
    setEditing({
      ...d,
      price_current: d.price_current ?? "",
      price_original: d.price_original ?? "",
      discount_percent: d.discount_percent ?? "",
      expires_at: d.expires_at ? d.expires_at.slice(0, 16) : "",
    });
    setAutoFillMsg(null);
    setShowForm(true);
  };

  const saveDeal = async () => {
    const payload = {
      ...editing,
      price_current: editing.price_current === "" ? null : parseFloat(editing.price_current),
      price_original: editing.price_original === "" ? null : parseFloat(editing.price_original),
      discount_percent: editing.discount_percent === "" ? null : parseInt(editing.discount_percent),
      expires_at: editing.expires_at ? new Date(editing.expires_at).toISOString() : null,
    };
    try {
      if (editing.id) {
        await api.put(`/admin/deals/${editing.id}`, payload);
      } else {
        await api.post("/admin/deals", payload);
      }
      setShowForm(false);
      setEditing(null);
      reloadDeals();
    } catch (e) {
      if (e?.response?.status === 409) {
        const det = e.response.data?.detail || {};
        const msg = det.message || "Questa offerta è già presente sul sito.";
        const existingTitle = det.existing_title ? ` (esistente: "${det.existing_title}")` : "";
        alert(`${msg}${existingTitle}`);
      } else {
        alert("Errore salvataggio: " + (e?.response?.data?.detail?.message || e?.response?.data?.detail || e.message));
      }
    }
  };

  const removeDeal = async (id) => {
    if (!window.confirm("Eliminare definitivamente questa offerta?")) return;
    await api.delete(`/admin/deals/${id}`);
    reloadDeals();
  };

  const generateAnalisi = async () => {
    if (!editing) return;
    if (!editing.id) {
      setAnalisiMsg("Salva prima l'offerta, poi rigenera l'analisi.");
      return;
    }
    setGeneratingAnalisi(true);
    setAnalisiMsg("");
    try {
      const r = await api.post(`/admin/deals/${editing.id}/ai-analisi`);
      setEditing((prev) => ({ ...prev, analisi_breve: r.data.analisi_breve || "" }));
      setAnalisiMsg("Analisi generata. Modificala se necessario, poi salva.");
    } catch (e) {
      setAnalisiMsg("Errore: " + (e?.response?.data?.detail || e.message));
    } finally {
      setGeneratingAnalisi(false);
    }
  };

  // Poll batch progress while a batch is running
  useEffect(() => {
    if (!batchAnalisi?.running) return;
    const t = setInterval(async () => {
      try {
        const r = await api.get("/admin/deals/ai-analisi-batch/status");
        setBatchAnalisi(r.data);
        if (!r.data.running) {
          clearInterval(t);
          reloadDeals();
        }
      } catch (e) {
        clearInterval(t);
      }
    }, 2000);
    return () => clearInterval(t);
  }, [batchAnalisi?.running]);

  // Resume polling on mount if a batch is already in progress (e.g. user reopened admin)
  useEffect(() => {
    if (!hasToken) return;
    api.get("/admin/deals/ai-analisi-batch/status").then((r) => {
      if (r.data?.running) setBatchAnalisi(r.data);
    }).catch(() => {});
  }, [hasToken]);

  const startBatchAnalisi = async () => {
    const ok = window.confirm(
      "Genera automaticamente l'analisi editoriale per tutte le offerte pubblicate che ne sono ancora prive.\n\nPuò richiedere alcuni minuti (circa 10-15s per offerta).\nPuoi continuare a usare l'admin nel frattempo.\n\nProcedere?"
    );
    if (!ok) return;
    try {
      const r = await api.post("/admin/deals/ai-analisi-batch?only_missing=true&only_published=true");
      if (!r.data.started) {
        alert(r.data.message || "Niente da fare.");
        return;
      }
      setBatchAnalisi({ running: true, total: r.data.queued, done: 0, ok: 0, failed: 0, current_title: "" });
    } catch (e) {
      if (e?.response?.status === 409) {
        const s = await api.get("/admin/deals/ai-analisi-batch/status");
        setBatchAnalisi(s.data);
      } else {
        alert("Errore: " + (e?.response?.data?.detail || e.message));
      }
    }
  };

  // Generic batch runner used by the 3 new batch buttons (content / articles / categories).
  const startBatchGeneric = async ({
    url,
    statusUrl,
    setState,
    confirmMsg,
    body = {},
  }) => {
    if (!window.confirm(confirmMsg)) return;
    try {
      const r = await api.post(url, body);
      if (!r.data.started) {
        alert(r.data.message || "Niente da fare.");
        return;
      }
      setState({ running: true, total: r.data.queued, done: 0, ok: 0, failed: 0, current_title: "", current_key: "" });
    } catch (e) {
      if (e?.response?.status === 409) {
        const s = await api.get(statusUrl);
        setState(s.data);
      } else {
        // FastAPI validation errors return `detail` as an array of objects — normalize to string.
        const d = e?.response?.data?.detail;
        const msg = Array.isArray(d)
          ? d.map((it) => it?.msg || JSON.stringify(it)).join(" · ")
          : (typeof d === "string" ? d : (d ? JSON.stringify(d) : e.message));
        alert("Errore: " + msg);
      }
    }
  };

  useEffect(() => {
    if (!batchContent?.running) return;
    const t = setInterval(async () => {
      try {
        const r = await api.get("/admin/deals/ai-content-batch/status");
        setBatchContent(r.data);
        if (!r.data.running) {
          clearInterval(t);
          reloadDeals();
        }
      } catch { clearInterval(t); }
    }, 2500);
    return () => clearInterval(t);
  }, [batchContent?.running]);

  useEffect(() => {
    if (!batchArticles?.running) return;
    const t = setInterval(async () => {
      try {
        const r = await api.get("/admin/articles/ai-batch-status");
        setBatchArticles(r.data);
        if (!r.data.running) clearInterval(t);
      } catch { clearInterval(t); }
    }, 3000);
    return () => clearInterval(t);
  }, [batchArticles?.running]);

  useEffect(() => {
    if (!batchCategories?.running) return;
    const t = setInterval(async () => {
      try {
        const r = await api.get("/admin/categories/ai-intro-batch-status");
        setBatchCategories(r.data);
        if (!r.data.running) clearInterval(t);
      } catch { clearInterval(t); }
    }, 2500);
    return () => clearInterval(t);
  }, [batchCategories?.running]);

  const startBatchContent = () => startBatchGeneric({
    url: "/admin/deals/ai-content-batch?only_missing=true&only_published=true",
    statusUrl: "/admin/deals/ai-content-batch/status",
    setState: setBatchContent,
    confirmMsg: "Genera contenuto editoriale completo (Perché comprarlo · Dettagli · Prezzo · FAQ) per tutte le offerte pubblicate che ne sono ancora prive.\n\nRichiede ~15-20 secondi per offerta. Puoi continuare a usare l'admin.\n\nProcedere?",
  });

  const startBatchArticles = () => startBatchGeneric({
    url: "/admin/articles/ai-batch",
    statusUrl: "/admin/articles/ai-batch-status",
    setState: setBatchArticles,
    confirmMsg: "Genera in batch 20 articoli editoriali (800-1200 parole) dalla libreria di argomenti curati.\n\nRichiede ~20 secondi per articolo (~7 minuti totali).\nGli articoli con slug già esistente vengono saltati automaticamente.\n\nProcedere?",
  });

  const startBatchCategories = () => startBatchGeneric({
    url: "/admin/categories/ai-intro-batch?only_missing=true",
    statusUrl: "/admin/categories/ai-intro-batch-status",
    setState: setBatchCategories,
    confirmMsg: "Genera un'introduzione editoriale approfondita (~230 parole) per ogni categoria che ne è priva.\n\nRichiede ~10 secondi per categoria. 16 categorie da coprire.\n\nProcedere?",
  });

  const pushDeal = async (id) => {
    try {
      const r = await api.post(`/admin/deals/${id}/push`);
      alert(`Notifica inviata a ${r.data.sent || 0} utenti${r.data.dead ? ` (${r.data.dead} obsolete rimosse)` : ""}`);
    } catch (e) {
      alert("Errore push: " + (e?.response?.data?.detail || e.message));
    }
  };

  const postToTelegram = async (id) => {
    if (!window.confirm("Pubblicare questa offerta sul canale Telegram?")) return;
    try {
      const r = await api.post(`/admin/deals/${id}/post-telegram`);
      if (r.data.ok) {
        alert(`Pubblicato su Telegram! (message id: ${r.data.message_id})`);
        reloadDeals();
      } else if (r.data.skipped) {
        alert(`Saltato: ${r.data.reason}`);
      } else {
        alert("Errore Telegram: " + (r.data.error || "sconosciuto"));
      }
    } catch (e) {
      alert("Errore Telegram: " + (e?.response?.data?.detail || e.message));
    }
  };

  const triggerSync = async () => {
    setSyncResult({ loading: true });
    try {
      const r = await api.post("/admin/telegram/sync");
      setSyncResult(r.data);
      reloadDeals();
      reloadSettings();
    } catch (e) {
      setSyncResult({ ok: false, error: e?.response?.data?.detail || "Errore sync" });
    }
  };

  const [backfillResult, setBackfillResult] = useState(null);
  const backfillImages = async () => {
    setBackfillResult({ loading: true });
    try {
      const r = await api.post("/admin/deals/backfill-images?limit=100");
      setBackfillResult(r.data);
      reloadDeals();
    } catch (e) {
      setBackfillResult({ ok: false, error: e?.response?.data?.detail || "Errore backfill" });
    }
  };

  const duplicateDeal = async (id) => {
    try {
      const r = await api.post(`/admin/deals/${id}/duplicate`);
      reloadDeals();
      openEdit(r.data);
    } catch (e) {
      alert("Errore duplicazione: " + (e?.response?.data?.detail || e.message));
    }
  };

  const togglePublished = async (d) => {
    try {
      await api.put(`/admin/deals/${d.id}`, { published: !d.published });
      reloadDeals();
    } catch (e) {
      alert("Errore: " + (e?.response?.data?.detail || e.message));
    }
  };

  // ---- Bulk selection ----
  const [selected, setSelected] = useState(() => new Set());
  const toggleSel = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };
  const toggleSelAll = () => {
    setSelected((prev) => {
      if (prev.size === deals.length) return new Set();
      return new Set(deals.map((d) => d.id));
    });
  };
  const clearSel = () => setSelected(new Set());

  const bulk = async (action) => {
    if (selected.size === 0) return;
    if (action === "delete" && !window.confirm(`Eliminare ${selected.size} offerte selezionate?`)) return;
    try {
      await api.post("/admin/deals/bulk", {
        ids: Array.from(selected),
        action,
      });
      clearSel();
      reloadDeals();
    } catch (e) {
      alert("Errore bulk: " + (e?.response?.data?.detail || e.message));
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const payload = { ...settings };
      delete payload.telegram_bot_token_masked;
      delete payload.last_update_id;
      delete payload.last_sync_at;
      await api.put("/admin/settings", payload);
      await reloadSettings();
    } finally {
      setSavingSettings(false);
    }
  };

  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const runDiagnostics = async () => {
    setDiagLoading(true);
    try {
      const r = await api.get("/admin/telegram/diagnostics");
      setDiag(r.data);
    } catch (e) {
      setDiag({ error: e?.response?.data?.detail || "Errore" });
    } finally {
      setDiagLoading(false);
    }
  };

  // ---- Import via URL ----
  const [showImport, setShowImport] = useState(false);
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState("");

  // ---- Auto-fill inside Nuova/Modifica offerta from the affiliate URL ----
  const [autoFilling, setAutoFilling] = useState(false);
  const [autoFillMsg, setAutoFillMsg] = useState(null);
  const autoFillFromAffiliate = async (rawUrl) => {
    const url = (rawUrl || "").trim();
    if (!url || !/^https?:\/\//i.test(url)) {
      setAutoFillMsg({ ok: false, text: "Inserisci prima un URL valido (https://…)" });
      return;
    }
    setAutoFilling(true);
    setAutoFillMsg(null);
    try {
      const r = await api.post("/admin/import/scrape", { url });
      const d = r.data || {};
      if (d.duplicate_of && d.duplicate_of.id && !editing?.id) {
        setAutoFillMsg({
          ok: false,
          text: `Attenzione: esiste già un'offerta con questo URL ("${d.duplicate_of.title}").`,
        });
      }
      setEditing((prev) => ({
        ...prev,
        title: prev.title || d.title || "",
        description: prev.description || d.description || "",
        image_url: prev.image_url || d.image_url || "",
        affiliate_url: d.url || url,
        store: d.store || prev.store || "altro",
        category: prev.category && prev.category !== "altro" ? prev.category : (d.category || "altro"),
        price_current: (prev.price_current !== "" && prev.price_current != null) ? prev.price_current : (d.price_current ?? ""),
      }));
      const quality = d.scrape_quality;
      setAutoFillMsg(
        quality === "ok"
          ? { ok: true, text: "Campi compilati automaticamente. Controlla e modifica se necessario." }
          : { ok: true, text: "Compilazione parziale — alcuni campi vanno completati a mano (prezzo, sconto)." }
      );
    } catch (e) {
      setAutoFillMsg({
        ok: false,
        text: "Errore lettura URL: " + (e?.response?.data?.detail || e.message),
      });
    } finally {
      setAutoFilling(false);
    }
  };

  const runImport = async () => {
    if (!importUrl.trim()) return;
    setImporting(true);
    setImportError("");
    try {
      const r = await api.post("/admin/import/scrape", { url: importUrl.trim() });
      const d = r.data;
      // Duplicate guard — block creating a second copy of the same URL
      if (d.duplicate_of && d.duplicate_of.id) {
        const dup = d.duplicate_of;
        const goEdit = window.confirm(
          `Questa offerta è già presente sul sito:\n\n"${dup.title}"\n\nVuoi aprirla in modifica?`
        );
        if (goEdit) {
          // Fetch existing deal and open edit form
          try {
            const exi = deals.find((x) => x.id === dup.id);
            if (exi) openEdit(exi);
          } catch (err) {
            console.warn("open existing failed", err);
          }
        }
        setShowImport(false);
        setImportUrl("");
        return;
      }
      setEditing({
        ...emptyDeal,
        title: d.title || "",
        description: d.description || "",
        image_url: d.image_url || "",
        affiliate_url: d.url || importUrl.trim(),
        store: d.store || "amazon",
        category: d.category || "altro",
        price_current: d.price_current ?? "",
        price_original: "",
        discount_percent: "",
        hot: false,
        published: true,
      });
      setShowImport(false);
      setShowForm(true);
      setImportUrl("");
    } catch (e) {
      setImportError(e?.response?.data?.detail || "Errore di import");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950">
      <div className="border-b border-zinc-900 bg-zinc-950/80 backdrop-blur sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Logo />
            <span className="hidden sm:inline-block font-mono-bdd text-[10px] uppercase tracking-[0.25em] text-[#FBD51C] bg-zinc-900 border border-zinc-800 rounded-full px-3 py-1">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" className="text-xs text-zinc-400 hover:text-white" data-testid="admin-view-site">Vedi sito</Link>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-white text-xs font-bold px-3 py-2 rounded-lg"
            >
              <LogOut size={12} /> Esci
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* tabs - scroll horizontal on mobile */}
        <div className="-mx-4 sm:mx-0 px-4 sm:px-0 mb-6 overflow-x-auto">
          <div className="flex items-center gap-2 min-w-max">
            <button
              data-testid="tab-deals"
              onClick={() => setTab("deals")}
              className={`shrink-0 px-4 py-2 rounded-lg text-sm font-bold ${tab === "deals" ? "bg-[#FBD51C] text-black" : "bg-zinc-900 text-zinc-400 border border-zinc-800"}`}
            >
              Offerte ({deals.length})
            </button>
            <button
              data-testid="tab-stats"
              onClick={() => setTab("stats")}
              className={`shrink-0 px-4 py-2 rounded-lg text-sm font-bold ${tab === "stats" ? "bg-[#FBD51C] text-black" : "bg-zinc-900 text-zinc-400 border border-zinc-800"}`}
            >
              📊 Statistiche
            </button>
            <button
              data-testid="tab-telegram"
              onClick={() => setTab("telegram")}
              className={`shrink-0 px-4 py-2 rounded-lg text-sm font-bold ${tab === "telegram" ? "bg-[#FBD51C] text-black" : "bg-zinc-900 text-zinc-400 border border-zinc-800"}`}
            >
              Telegram
            </button>
            <button
              data-testid="tab-settings"
              onClick={() => setTab("settings")}
              className={`shrink-0 px-4 py-2 rounded-lg text-sm font-bold ${tab === "settings" ? "bg-[#FBD51C] text-black" : "bg-zinc-900 text-zinc-400 border border-zinc-800"}`}
            >
              <SettingsIcon size={14} className="inline mr-1" /> Impostazioni
            </button>
            <button
              data-testid="tab-messages"
              onClick={() => setTab("messages")}
              className={`shrink-0 px-4 py-2 rounded-lg text-sm font-bold ${tab === "messages" ? "bg-[#FBD51C] text-black" : "bg-zinc-900 text-zinc-400 border border-zinc-800"}`}
            >
              ✉️ Messaggi
            </button>
            <button
              data-testid="tab-blog"
              onClick={() => setTab("blog")}
              className={`shrink-0 px-4 py-2 rounded-lg text-sm font-bold ${tab === "blog" ? "bg-[#FBD51C] text-black" : "bg-zinc-900 text-zinc-400 border border-zinc-800"}`}
            >
              📝 Blog
            </button>
            <button
              data-testid="tab-ebay"
              onClick={() => setTab("ebay")}
              className={`shrink-0 px-4 py-2 rounded-lg text-sm font-bold ${tab === "ebay" ? "bg-[#FBD51C] text-black" : "bg-zinc-900 text-zinc-400 border border-zinc-800"}`}
            >
              🏷️ eBay Import
            </button>
            <button
              data-testid="tab-newsletter"
              onClick={() => setTab("newsletter")}
              className={`shrink-0 px-4 py-2 rounded-lg text-sm font-bold ${tab === "newsletter" ? "bg-[#FBD51C] text-black" : "bg-zinc-900 text-zinc-400 border border-zinc-800"}`}
            >
              ✉️ Newsletter
            </button>
          </div>
        </div>

        {tab === "deals" && (
          <div>
            <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
              <div>
                <h1 className="font-display font-black text-2xl text-white">Gestione offerte</h1>
                <p className="text-zinc-500 text-sm">Crea, modifica o elimina le offerte pubblicate.</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="admin-import-url"
                  onClick={() => setShowImport(true)}
                  className="inline-flex items-center gap-2 bg-[#131e3a] hover:bg-[#1a2647] border border-[#1f2c4a] text-white font-bold text-xs uppercase tracking-wider px-4 py-2.5 rounded-lg"
                >
                  <Link2 size={14} /> Importa da URL
                </button>
                <button
                  data-testid="admin-create-deal"
                  onClick={openCreate}
                  className="inline-flex items-center gap-2 bg-[#FBD51C] hover:bg-[#FFE552] text-black font-black uppercase tracking-wider text-xs px-4 py-2.5 rounded-lg brand-cta-glow"
                >
                  <Plus size={14} /> Nuova offerta
                </button>
                <button
                  data-testid="admin-upscale-images"
                  onClick={async () => {
                    if (!window.confirm("Migliorare la risoluzione di tutte le immagini esistenti? Aggiorna eBay/Amazon/AliExpress all'HD massimo disponibile. Operazione sicura, può essere ripetuta.")) return;
                    try {
                      const r = await api.post("/admin/deals/upscale-images");
                      alert(`Immagini aggiornate: ${r.data.updated} su ${r.data.scanned} offerte scansionate.`);
                      reloadDeals();
                    } catch (e) {
                      alert("Errore: " + (e?.response?.data?.detail || e.message));
                    }
                  }}
                  title="Riscrive le URL immagine in HD massima (eBay 1600px, Amazon 1500px, AliExpress originale)"
                  className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-300 hover:text-white text-xs font-bold px-3 py-2.5 rounded-lg"
                >
                  <Image size={12} /> HD immagini
                </button>
                <button
                  data-testid="admin-amazon-hd-refresh"
                  onClick={async () => {
                    if (!window.confirm("Scarica le immagini HD reali (1500px) dalla pagina prodotto Amazon per tutte le offerte Amazon con immagini ancora a bassa risoluzione. Può richiedere 30-60 secondi.")) return;
                    try {
                      const r = await api.post("/admin/deals/amazon-hd-refresh");
                      alert(`Amazon HD: ${r.data.updated} aggiornate · ${r.data.failed} non recuperabili · ${r.data.scanned} scansionate.`);
                      reloadDeals();
                    } catch (e) {
                      alert("Errore: " + (e?.response?.data?.detail || e.message));
                    }
                  }}
                  title="Scrape pagina prodotto Amazon ed estrae l'immagine landing HD 1500px reale"
                  className="inline-flex items-center gap-2 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 text-zinc-300 hover:text-white text-xs font-bold px-3 py-2.5 rounded-lg"
                >
                  <Image size={12} /> HD Amazon
                </button>
                <button
                  data-testid="admin-batch-analisi"
                  onClick={startBatchAnalisi}
                  disabled={batchAnalisi?.running}
                  title="Genera con AI le analisi editoriali mancanti su tutte le offerte pubblicate (60-90 parole ciascuna). Aiuta l'approvazione AdSense."
                  className="inline-flex items-center gap-2 bg-[#FBD51C]/15 hover:bg-[#FBD51C]/25 disabled:opacity-50 border border-[#FBD51C]/40 text-[#FBD51C] text-xs font-bold px-3 py-2.5 rounded-lg"
                >
                  <Sparkles size={12} />
                  {batchAnalisi?.running
                    ? `AI in corso… ${batchAnalisi.done}/${batchAnalisi.total}`
                    : "Auto-AI Analisi"}
                </button>
                <button
                  data-testid="admin-batch-content"
                  onClick={startBatchContent}
                  disabled={batchContent?.running}
                  title="Genera contenuto editoriale completo (Perché · Dettagli · Prezzo · FAQ) su tutte le offerte pubblicate che ne sono prive. Massimo boost AdSense."
                  className="inline-flex items-center gap-2 bg-emerald-500/15 hover:bg-emerald-500/25 disabled:opacity-50 border border-emerald-500/40 text-emerald-300 text-xs font-bold px-3 py-2.5 rounded-lg"
                >
                  <Sparkles size={12} />
                  {batchContent?.running
                    ? `Contenuto AI… ${batchContent.done}/${batchContent.total}`
                    : "Auto-AI Contenuto"}
                </button>
                <button
                  data-testid="admin-batch-articles"
                  onClick={startBatchArticles}
                  disabled={batchArticles?.running}
                  title="Genera 20 articoli editoriali (~800-1200 parole ciascuno) dalla libreria curata. Amplia il blog per l'approvazione AdSense."
                  className="inline-flex items-center gap-2 bg-sky-500/15 hover:bg-sky-500/25 disabled:opacity-50 border border-sky-500/40 text-sky-300 text-xs font-bold px-3 py-2.5 rounded-lg"
                >
                  <Sparkles size={12} />
                  {batchArticles?.running
                    ? `Articoli AI… ${batchArticles.done}/${batchArticles.total}`
                    : "Auto-AI Articoli"}
                </button>
                <button
                  data-testid="admin-batch-categories"
                  onClick={startBatchCategories}
                  disabled={batchCategories?.running}
                  title="Genera introduzioni editoriali approfondite (~230 parole) per le pagine categoria."
                  className="inline-flex items-center gap-2 bg-purple-500/15 hover:bg-purple-500/25 disabled:opacity-50 border border-purple-500/40 text-purple-300 text-xs font-bold px-3 py-2.5 rounded-lg"
                >
                  <Sparkles size={12} />
                  {batchCategories?.running
                    ? `Categorie… ${batchCategories.done}/${batchCategories.total}`
                    : "Auto-AI Categorie"}
                </button>
              </div>
            </div>

            {batchAnalisi && (batchAnalisi.running || (batchAnalisi.total > 0 && batchAnalisi.done >= batchAnalisi.total)) ? (
              <div className="mt-3 p-3 rounded-lg bg-[#0d162d]/60 border border-[#1f2c4a]" data-testid="batch-analisi-progress">
                <div className="flex items-center justify-between mb-2 text-xs">
                  <div className="font-mono-bdd uppercase tracking-wider text-[#FBD51C]">
                    {batchAnalisi.running ? "Generazione analisi AI in corso" : "Generazione completata"}
                  </div>
                  <div className="text-zinc-400">
                    {batchAnalisi.done}/{batchAnalisi.total}
                    {" · "}
                    <span className="text-emerald-400">{batchAnalisi.ok} ok</span>
                    {batchAnalisi.failed > 0 && (
                      <>
                        {" · "}
                        <span className="text-rose-400">{batchAnalisi.failed} falliti</span>
                      </>
                    )}
                  </div>
                </div>
                <div className="h-1.5 rounded-full bg-zinc-900 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#FBD51C] to-[#F5C518] transition-all duration-500"
                    style={{ width: `${batchAnalisi.total ? Math.round((batchAnalisi.done / batchAnalisi.total) * 100) : 0}%` }}
                  />
                </div>
                {batchAnalisi.current_title && batchAnalisi.running ? (
                  <div className="mt-2 text-[11px] text-zinc-500 truncate">
                    In lavorazione: <span className="text-zinc-300">{batchAnalisi.current_title}</span>
                  </div>
                ) : null}
                {!batchAnalisi.running && batchAnalisi.last_error ? (
                  <div className="mt-2 text-[11px] text-rose-400 truncate">
                    Ultimo errore: {batchAnalisi.last_error}
                  </div>
                ) : null}
              </div>
            ) : null}

            {batchContent && (batchContent.running || (batchContent.total > 0 && batchContent.done >= batchContent.total)) ? (
              <ProgressBar label="Contenuto editoriale AI" state={batchContent} accent="emerald" testid="batch-content-progress" />
            ) : null}
            {batchArticles && (batchArticles.running || (batchArticles.total > 0 && batchArticles.done >= batchArticles.total)) ? (
              <ProgressBar label="Generazione articoli blog" state={batchArticles} accent="sky" testid="batch-articles-progress" />
            ) : null}
            {batchCategories && (batchCategories.running || (batchCategories.total > 0 && batchCategories.done >= batchCategories.total)) ? (
              <ProgressBar label="Intro categorie" state={batchCategories} accent="purple" testid="batch-categories-progress" keyField="current_key" />
            ) : null}

            {loading ? (
              <div className="space-y-2">
                {Array.from({length:5}).map((_,i)=><div key={i} className="h-16 rounded-lg shimmer" />)}
              </div>
            ) : (
              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
                {/* Source filter chips */}
                <div className="flex items-center gap-2 px-4 py-3 border-b border-zinc-800 flex-wrap text-xs">
                  <span className="text-zinc-500 font-mono-bdd uppercase tracking-wider text-[10px]">Provenienza:</span>
                  {[
                    { key: "all", label: "Tutte", icon: "🗂️" },
                    { key: "manual", label: "Manuali", icon: "✍️" },
                    { key: "telegram", label: "Telegram", icon: "📨" },
                    { key: "ebay_api", label: "eBay API", icon: "🏷️" },
                  ].map((s) => {
                    const count = s.key === "all" ? allDeals.length : (sourceCounts[s.key] || 0);
                    const active = sourceFilter === s.key;
                    return (
                      <button
                        key={s.key}
                        data-testid={`source-filter-${s.key}`}
                        onClick={() => setSourceFilter(s.key)}
                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full font-bold transition ${
                          active
                            ? "bg-[#FBD51C] text-black"
                            : "bg-zinc-950 text-zinc-400 border border-zinc-800 hover:text-white"
                        }`}
                      >
                        <span>{s.icon}</span>
                        {s.label}
                        <span className={`text-[10px] ${active ? "text-black/70" : "text-zinc-500"}`}>({count})</span>
                      </button>
                    );
                  })}
                </div>
                {selected.size > 0 && (
                  <div
                    data-testid="bulk-actions"
                    className="flex items-center gap-2 px-4 py-3 bg-[#FBD51C]/10 border-b border-[#FBD51C]/30 flex-wrap"
                  >
                    <button
                      onClick={clearSel}
                      data-testid="bulk-clear"
                      className="text-zinc-400 hover:text-white"
                    >
                      <X size={16} />
                    </button>
                    <span className="text-sm text-white font-bold">
                      {selected.size} selezionate
                    </span>
                    <div className="ml-auto flex flex-wrap items-center gap-2">
                      <button
                        data-testid="bulk-publish"
                        onClick={() => bulk("publish")}
                        className="inline-flex items-center gap-1.5 bg-emerald-500/15 hover:bg-emerald-500/25 border border-emerald-700/40 text-emerald-300 text-xs font-bold px-3 py-1.5 rounded-lg"
                      >
                        <Eye size={12} /> Pubblica
                      </button>
                      <button
                        data-testid="bulk-unpublish"
                        onClick={() => bulk("unpublish")}
                        className="inline-flex items-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-300 text-xs font-bold px-3 py-1.5 rounded-lg"
                      >
                        <EyeOff size={12} /> Nascondi
                      </button>
                      <button
                        data-testid="bulk-hot"
                        onClick={() => bulk("mark_hot")}
                        className="inline-flex items-center gap-1.5 bg-rose-500/15 hover:bg-rose-500/25 border border-rose-700/40 text-rose-300 text-xs font-bold px-3 py-1.5 rounded-lg"
                      >
                        <Flame size={12} /> HOT
                      </button>
                      <button
                        data-testid="bulk-unhot"
                        onClick={() => bulk("unmark_hot")}
                        className="text-xs font-bold text-zinc-400 hover:text-white px-3 py-1.5 rounded-lg"
                      >
                        Rimuovi HOT
                      </button>
                      <button
                        data-testid="bulk-delete"
                        onClick={() => bulk("delete")}
                        className="inline-flex items-center gap-1.5 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold px-3 py-1.5 rounded-lg"
                      >
                        <Trash2 size={12} /> Elimina
                      </button>
                    </div>
                  </div>
                )}
                <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[800px]" data-testid="admin-deals-table">
                  <thead className="bg-zinc-950 text-zinc-500 font-mono-bdd text-[10px] uppercase tracking-wider">
                    <tr>
                      <th className="px-3 py-3 w-8">
                        <input
                          data-testid="bulk-select-all"
                          type="checkbox"
                          className="accent-[#FBD51C]"
                          checked={deals.length > 0 && selected.size === deals.length}
                          onChange={toggleSelAll}
                        />
                      </th>
                      <th className="px-4 py-3 text-left">Offerta</th>
                      <th className="px-4 py-3 text-left">Store</th>
                      <th className="px-4 py-3 text-left">Categoria</th>
                      <th className="px-4 py-3 text-right">Prezzo</th>
                      <th className="px-4 py-3 text-right">Sconto</th>
                      <th className="px-4 py-3 text-right">Click</th>
                      <th className="px-4 py-3 text-center">Stato</th>
                      <th className="px-4 py-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {deals.map((d) => (
                      <tr
                        key={d.id}
                        className={`border-t border-zinc-800 hover:bg-zinc-950/60 ${
                          selected.has(d.id) ? "bg-[#FBD51C]/5" : ""
                        }`}
                      >
                        <td className="px-3 py-3">
                          <input
                            data-testid={`bulk-select-${d.id}`}
                            type="checkbox"
                            className="accent-[#FBD51C]"
                            checked={selected.has(d.id)}
                            onChange={() => toggleSel(d.id)}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 bg-zinc-950 rounded-lg overflow-hidden shrink-0 brick-pattern">
                              {d.image_url ? (
                                <img src={fileUrl(d.image_url)} alt="" className="w-full h-full object-cover" />
                              ) : null}
                            </div>
                            <div>
                              <div className="text-white font-bold line-clamp-1 max-w-[280px]" title={d.title}>{dealTitle(d)}</div>
                              <div className="flex items-center gap-2 mt-0.5">
                                <a href={d.affiliate_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-zinc-500 inline-flex items-center gap-1 hover:text-[#FBD51C]">
                                  Link <ExternalLink size={9} />
                                </a>
                                <SourceBadge source={d.source} />
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-zinc-300">{STORE_LABEL[d.store]}</td>
                        <td className="px-4 py-3 text-zinc-400">{d.category}</td>
                        <td className="px-4 py-3 text-right text-white font-bold">{d.price_current ? priceFmt(d.price_current) : "—"}</td>
                        <td className="px-4 py-3 text-right text-rose-400 font-bold">{d.discount_percent ? `-${d.discount_percent}%` : "—"}</td>
                        <td className="px-4 py-3 text-right text-zinc-400">{d.clicks}</td>
                        <td className="px-4 py-3 text-center">
                          <button
                            data-testid={`admin-publish-${d.id}`}
                            onClick={() => togglePublished(d)}
                            title={d.published ? "Click per nascondere" : "Click per pubblicare"}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-mono-bdd uppercase tracking-wider transition-colors ${
                              d.published
                                ? "bg-emerald-500/15 text-emerald-300 border border-emerald-700/40 hover:bg-emerald-500/25"
                                : "bg-zinc-800/60 text-zinc-400 border border-zinc-700 hover:bg-zinc-700"
                            }`}
                          >
                            <span
                              className={`w-1.5 h-1.5 rounded-full ${
                                d.published ? "bg-emerald-400" : "bg-zinc-500"
                              }`}
                            />
                            {d.published ? "Live" : "Bozza"}
                            {d.hot && <Flame size={11} className="text-rose-400" />}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <button data-testid={`admin-push-${d.id}`} onClick={() => pushDeal(d.id)} className="text-zinc-400 hover:text-[#FBD51C] p-1.5" title="Invia notifica push">
                            <Bell size={14} />
                          </button>
                          <button
                            data-testid={`admin-telegram-${d.id}`}
                            onClick={() => postToTelegram(d.id)}
                            className={`p-1.5 ${d.telegram_posted_at ? "text-[#2AABEE]" : "text-zinc-400 hover:text-[#2AABEE]"}`}
                            title={d.telegram_posted_at ? `Già pubblicato su Telegram il ${new Date(d.telegram_posted_at).toLocaleString("it-IT")}. Click per ripubblicare.` : "Pubblica su Telegram"}
                          >
                            <Send size={14} />
                          </button>
                          <button data-testid={`admin-duplicate-${d.id}`} onClick={() => duplicateDeal(d.id)} className="text-zinc-400 hover:text-[#FBD51C] p-1.5" title="Duplica offerta">
                            <Copy size={14} />
                          </button>
                          <button data-testid={`admin-edit-${d.id}`} onClick={() => openEdit(d)} className="text-zinc-400 hover:text-white p-1.5" title="Modifica">
                            <Pencil size={14} />
                          </button>
                          <button data-testid={`admin-delete-${d.id}`} onClick={() => removeDeal(d.id)} className="text-zinc-400 hover:text-rose-400 p-1.5" title="Elimina">
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                    {deals.length === 0 && (
                      <tr><td colSpan={9} className="text-center text-zinc-500 py-10">Nessuna offerta</td></tr>
                    )}
                  </tbody>
                </table>
                </div>
              </div>
            )}
          </div>
        )}

        {tab === "stats" && <StatsTab />}

        {tab === "telegram" && settings && (
          <div className="max-w-3xl">
            <h1 className="font-display font-black text-2xl text-white mb-1">Importazione Telegram</h1>
            <p className="text-zinc-500 text-sm mb-6">
              Collega il bot Telegram per importare automaticamente i post del canale.
            </p>

            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 space-y-5">
              <Field label="Token bot Telegram" hint="Ottieni da @BotFather. Il bot deve essere amministratore del canale.">
                <input
                  data-testid="settings-telegram-token"
                  type="password"
                  value={settings.telegram_bot_token || ""}
                  onChange={(e) => updateSettings({ telegram_bot_token: e.target.value })}
                  placeholder={settings.telegram_bot_token_masked || "12345678:AA..."}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-white outline-none focus:border-[#FBD51C]/60"
                />
              </Field>
              <Field label="Canale" hint="Username del canale es. @BrickDailyDeals">
                <input
                  data-testid="settings-telegram-channel"
                  value={settings.telegram_channel || ""}
                  onChange={(e) => updateSettings({ telegram_channel: e.target.value })}
                  placeholder="@BrickDailyDeals"
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-white outline-none focus:border-[#FBD51C]/60"
                />
              </Field>
              <div className="flex items-center gap-3">
                <label className="inline-flex items-center gap-2 text-sm text-zinc-300">
                  <input
                    data-testid="settings-polling-enabled"
                    type="checkbox"
                    checked={!!settings.polling_enabled}
                    onChange={(e) => updateSettings({ polling_enabled: e.target.checked })}
                    className="accent-[#FBD51C]"
                  />
                  Polling automatico
                </label>
                <input
                  data-testid="settings-polling-interval"
                  type="number"
                  min={30}
                  value={settings.polling_interval_seconds || 90}
                  onChange={(e) => updateSettings({ polling_interval_seconds: parseInt(e.target.value || 90) })}
                  className="w-24 bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-white outline-none"
                />
                <span className="text-xs text-zinc-500">secondi</span>
              </div>

              <div className="flex items-center gap-3 pt-3">
                <button
                  data-testid="settings-save"
                  onClick={saveSettings}
                  disabled={savingSettings}
                  className="inline-flex items-center gap-2 bg-[#FBD51C] text-black font-bold text-sm px-4 py-2 rounded-lg"
                >
                  <Save size={14} /> {savingSettings ? "Salvataggio..." : "Salva"}
                </button>
                <button
                  data-testid="telegram-sync-now"
                  onClick={triggerSync}
                  className="inline-flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700 text-sm font-bold px-4 py-2 rounded-lg"
                >
                  <RefreshCw size={14} /> Sincronizza adesso
                </button>
                <button
                  data-testid="backfill-images-btn"
                  onClick={backfillImages}
                  disabled={backfillResult?.loading}
                  title="Per le offerte senza immagine, recupera la foto dall'URL prodotto (Open Graph)"
                  className="inline-flex items-center gap-2 bg-zinc-800 hover:bg-zinc-700 text-white border border-zinc-700 text-sm font-bold px-4 py-2 rounded-lg disabled:opacity-50"
                >
                  <RefreshCw size={14} className={backfillResult?.loading ? "animate-spin" : ""} /> Recupera immagini mancanti
                </button>
              </div>

              {backfillResult && !backfillResult.loading && (
                <div
                  data-testid="backfill-result"
                  className={`text-sm rounded-lg p-3 border ${
                    backfillResult.ok
                      ? "border-emerald-900 bg-emerald-950/40 text-emerald-300"
                      : "border-rose-900 bg-rose-950/40 text-rose-300"
                  }`}
                >
                  {backfillResult.ok
                    ? `Recuperate ${backfillResult.fixed || 0} immagini su ${backfillResult.checked || 0} offerte controllate.`
                    : `Errore: ${backfillResult.error}`}
                </div>
              )}

              {syncResult && (
                <div
                  className={`flex items-start gap-2 text-sm rounded-lg p-3 border ${
                    syncResult.loading
                      ? "border-zinc-800 bg-zinc-950 text-zinc-400"
                      : syncResult.ok
                      ? "border-emerald-900 bg-emerald-950/40 text-emerald-300"
                      : "border-rose-900 bg-rose-950/40 text-rose-300"
                  }`}
                >
                  {syncResult.loading ? (
                    <RefreshCw size={14} className="animate-spin mt-0.5" />
                  ) : syncResult.ok ? (
                    <CheckCircle2 size={14} className="mt-0.5" />
                  ) : (
                    <AlertCircle size={14} className="mt-0.5" />
                  )}
                  <div data-testid="sync-result">
                    {syncResult.loading
                      ? "Sincronizzazione in corso..."
                      : syncResult.ok
                      ? `Importati ${syncResult.imported || 0} post (last update: ${syncResult.last_update_id})`
                      : `Errore: ${syncResult.error}`}
                  </div>
                </div>
              )}

              {settings.last_sync_at && (
                <div className="text-[10px] font-mono-bdd uppercase tracking-wider text-zinc-600">
                  Ultima sync: {new Date(settings.last_sync_at).toLocaleString("it-IT")}
                </div>
              )}
            </div>

            <div className="mt-6 p-5 bg-zinc-900/60 border border-zinc-800 rounded-2xl text-sm text-zinc-400 space-y-2">
              <div className="font-bold text-white">Come collegare il bot:</div>
              <ol className="list-decimal list-inside space-y-1">
                <li>Su Telegram apri <span className="text-[#FBD51C]">@BotFather</span> e crea un nuovo bot con <code className="text-white">/newbot</code>.</li>
                <li>Copia qui sopra il token che ti restituisce.</li>
                <li>Apri il tuo canale → Amministratori → Aggiungi amministratore → cerca il tuo bot e aggiungilo (anche con permessi minimi).</li>
                <li>Imposta il canale (es. <code className="text-white">@BrickDailyDeals</code>) e premi <em>Sincronizza adesso</em>.</li>
                <li>Attiva il polling automatico per importare in continuo.</li>
              </ol>
            </div>

            <div className="mt-4 p-5 bg-zinc-900/60 border border-zinc-800 rounded-2xl">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="font-bold text-white">Diagnostica</div>
                  <div className="text-xs text-zinc-500">Verifica stato bot, canale, updates pendenti</div>
                </div>
                <button
                  data-testid="telegram-diagnostics"
                  onClick={runDiagnostics}
                  disabled={diagLoading}
                  className="inline-flex items-center gap-2 bg-[#FBD51C] hover:bg-[#FFE552] text-black text-xs font-bold px-3 py-2 rounded-lg disabled:opacity-50"
                >
                  <RefreshCw size={12} className={diagLoading ? "animate-spin" : ""} /> Esegui controllo
                </button>
              </div>
              {diag && (
                <div data-testid="diag-result" className="text-xs space-y-2">
                  <DiagRow label="Token configurato" value={diag.bot_token_configured ? "✓ Sì" : "✗ No"} good={diag.bot_token_configured} />
                  {diag.bot && (
                    <DiagRow label="Bot" value={`@${diag.bot.username} (id ${diag.bot.id})`} good />
                  )}
                  {diag.webhook && (
                    <DiagRow
                      label="Webhook"
                      value={diag.webhook.url ? `⚠ ATTIVO → ${diag.webhook.url} (blocca il polling!)` : "✓ disattivato, polling OK"}
                      good={!diag.webhook.url}
                    />
                  )}
                  {diag.chat && (
                    <DiagRow label="Canale" value={`${diag.chat.title} (@${diag.chat.username})`} good />
                  )}
                  {diag.chat_error && (
                    <DiagRow label="Errore canale" value={diag.chat_error} good={false} />
                  )}
                  {diag.bot_membership && (
                    <DiagRow
                      label="Bot nel canale"
                      value={`status: ${diag.bot_membership.status}${
                        diag.bot_membership.status === "administrator"
                          ? " ✓ admin"
                          : " ⚠ non admin!"
                      }`}
                      good={diag.bot_membership.status === "administrator"}
                    />
                  )}
                  {diag.last_visible_update ? (
                    <DiagRow
                      label="Ultimo update in coda"
                      value={`msg_id ${diag.last_visible_update.message_id} · "${(diag.last_visible_update.text_preview || "").slice(0, 80)}..."`}
                      good
                    />
                  ) : (
                    <DiagRow
                      label="Updates pendenti"
                      value="Nessun update in coda. Telegram non ha consegnato ancora nessun post."
                      good={false}
                    />
                  )}
                  <DiagRow
                    label="Polling attivo"
                    value={diag.polling_enabled ? `✓ ogni ${diag.polling_interval_seconds}s` : "✗ disattivato"}
                    good={diag.polling_enabled}
                  />
                  {diag.last_sync_at && (
                    <DiagRow
                      label="Ultima sync"
                      value={new Date(diag.last_sync_at).toLocaleString("it-IT")}
                      good
                    />
                  )}
                  {diag.error && (
                    <DiagRow label="Errore" value={diag.error} good={false} />
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {tab === "settings" && settings && (
          <div className="max-w-2xl">
            <h1 className="font-display font-black text-2xl text-white mb-1">Impostazioni</h1>
            <p className="text-zinc-500 text-sm mb-6">Pubblicità e configurazione generale.</p>
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 space-y-5">
              <Field label="Google AdSense Client ID" hint="es. ca-pub-1234567890123456 — lasciato vuoto mostra placeholder.">
                <input
                  data-testid="settings-adsense"
                  value={settings.adsense_client_id || ""}
                  onChange={(e) => updateSettings({ adsense_client_id: e.target.value })}
                  placeholder="ca-pub-..."
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-white outline-none focus:border-[#FBD51C]/60"
                />
              </Field>
              <Field
                label="Soglia auto-push (sconto minimo)"
                hint="Quando una deal Telegram arriva con sconto ≥ a questo valore, viene inviata automaticamente la notifica push a tutti gli iscritti e marcata come HOT. Default: 20%"
              >
                <div className="flex items-center gap-3">
                  <input
                    data-testid="settings-autopush"
                    type="number"
                    min={0}
                    max={100}
                    value={settings.auto_push_min_discount ?? 20}
                    onChange={(e) =>
                      updateSettings({
                        auto_push_min_discount: parseInt(e.target.value || 0),
                      })
                    }
                    className="w-28 bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-white outline-none focus:border-[#FBD51C]/60"
                  />
                  <span className="text-sm text-zinc-400">% di sconto</span>
                </div>
              </Field>
              <Field
                label="Auto-pubblica su Telegram"
                hint="Quando pubblichi un'offerta dal pannello admin, il bot la posterà automaticamente sul canale Telegram (le offerte importate da Telegram vengono saltate per evitare loop)."
              >
                <label className="inline-flex items-center gap-3 cursor-pointer select-none">
                  <input
                    data-testid="settings-autopost-telegram"
                    type="checkbox"
                    checked={settings.telegram_autopost_on_publish !== false}
                    onChange={(e) =>
                      updateSettings({ telegram_autopost_on_publish: e.target.checked })
                    }
                    className="w-5 h-5 accent-[#2AABEE]"
                  />
                  <span className="text-sm text-zinc-300">
                    {settings.telegram_autopost_on_publish !== false ? "Attivo" : "Disattivato"}
                  </span>
                </label>
              </Field>
              <button
                data-testid="settings-general-save"
                onClick={saveSettings}
                className="inline-flex items-center gap-2 bg-[#FBD51C] hover:bg-[#FFE552] text-black font-black uppercase tracking-wider text-xs px-4 py-2.5 rounded-lg brand-cta-glow"
              >
                <Save size={14} /> Salva
              </button>
            </div>

            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 mt-4">
              <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <div>
                  <div className="font-display font-bold text-white">Notifiche push</div>
                  <div className="text-xs text-zinc-500">Verifica stato e invia una notifica di test a tutti gli iscritti</div>
                </div>
                <button
                  data-testid="push-test-btn"
                  onClick={async () => {
                    try {
                      const stats = await api.get("/admin/push/stats");
                      const r = await api.post("/admin/push/test");
                      let msg = `Iscritti: ${stats.data.subscribers}\nInviate: ${r.data.sent}\nObsolete rimosse: ${r.data.dead || 0}`;
                      if (r.data.errors && r.data.errors.length) {
                        msg += `\n\nErrori:\n${r.data.errors.join("\n")}`;
                      }
                      alert(msg);
                    } catch (e) {
                      alert("Errore: " + (e?.response?.data?.detail || e.message));
                    }
                  }}
                  className="inline-flex items-center gap-2 bg-[#FBD51C] hover:bg-[#FFE552] text-black font-black uppercase tracking-wider text-xs px-4 py-2 rounded-lg brand-cta-glow"
                >
                  <Bell size={14} /> Invia push di test
                </button>
              </div>
            </div>
          </div>
        )}

        {tab === "messages" && <MessagesTab />}
        {tab === "blog" && <BlogAdminTab />}
        {tab === "ebay" && <EbayAdminTab />}
        {tab === "newsletter" && <NewsletterAdminTab />}
      </div>

      {/* Import via URL modal */}
      {showImport && (
        <div
          data-testid="admin-import-modal"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
        >
          <div className="bg-[#131e3a] border border-[#1f2c4a] rounded-2xl w-full max-w-xl">
            <div className="px-6 py-4 border-b border-[#1f2c4a] flex items-center justify-between">
              <div>
                <h2 className="font-display font-black text-xl text-white flex items-center gap-2">
                  <Download size={18} className="text-[#FBD51C]" /> Importa da URL
                </h2>
                <p className="text-xs text-[#6e7896] mt-1">
                  Incolla il link Amazon, AliExpress, eBay o di qualunque sito: tentiamo di estrarre
                  titolo, immagine e prezzo automaticamente.
                </p>
              </div>
              <button
                onClick={() => {
                  setShowImport(false);
                  setImportUrl("");
                  setImportError("");
                }}
                className="text-zinc-400 hover:text-white text-sm"
              >
                Annulla
              </button>
            </div>
            <div className="p-6 space-y-4">
              <Field label="URL prodotto / offerta">
                <input
                  data-testid="import-url-input"
                  value={importUrl}
                  onChange={(e) => setImportUrl(e.target.value)}
                  placeholder="https://amzn.to/... oppure https://www.amazon.it/..."
                  autoFocus
                  className="w-full bg-[#0d162d] border border-[#1f2c4a] rounded-lg px-3 py-3 text-white outline-none focus:border-[#FBD51C]/60"
                />
              </Field>
              {importError && (
                <div
                  data-testid="import-error"
                  className="text-sm text-rose-400 bg-rose-950/40 border border-rose-900/60 rounded-lg px-3 py-2"
                >
                  {importError}
                </div>
              )}
              <div className="text-xs text-[#6e7896] bg-[#0d162d]/60 rounded-lg p-3 border border-[#1f2c4a]/60">
                💡 Per Amazon: usa preferibilmente l&apos;URL diretto (es. <code>https://www.amazon.it/dp/B0XXXXXX</code>)
                o il link affiliato corto <code>amzn.to/...</code>. Se lo scraping fallisce (Amazon a volte blocca),
                potrai sempre completare i campi manualmente nello step successivo.
              </div>
            </div>
            <div className="px-6 py-4 border-t border-[#1f2c4a] flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowImport(false);
                  setImportUrl("");
                  setImportError("");
                }}
                className="text-zinc-400 text-sm"
              >
                Annulla
              </button>
              <button
                data-testid="import-fetch"
                onClick={runImport}
                disabled={importing || !importUrl.trim()}
                className="inline-flex items-center gap-2 bg-[#FBD51C] hover:bg-[#FFE552] text-black font-black uppercase tracking-wider text-xs px-4 py-2.5 rounded-lg disabled:opacity-50 brand-cta-glow"
              >
                {importing ? (
                  <RefreshCw size={14} className="animate-spin" />
                ) : (
                  <Download size={14} />
                )}
                {importing ? "Estraggo..." : "Estrai dati"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {showForm && editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" data-testid="admin-deal-form">
          <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-900">
              <h2 className="font-display font-black text-xl text-white">
                {editing.id ? "Modifica offerta" : "Nuova offerta"}
              </h2>
              <button onClick={() => setShowForm(false)} className="text-zinc-500 hover:text-white text-sm">Annulla</button>
            </div>
            <div className="p-6 space-y-4">
              <Field
                label={
                  <span className="flex items-center justify-between gap-3">
                    <span>Titolo pubblico <span className="text-zinc-500 font-normal">(visualizzato sul sito)</span></span>
                    <button
                      type="button"
                      data-testid="btn-clean-title"
                      onClick={() => {
                        const raw = (editing.title || "").trim();
                        if (!raw) return;
                        // mirror server clean_title — strip marketplace boilerplate + truncate
                        const SUFFIX_RX = /(?:\s*[-–—:|,]\s*)(?:(?:in\s+su\s+)?Amazon(?:\.[a-z]{2,3})?(?:[:\s].*)?|Amaz[oa]?n?\.?|amzn\.to.*|eBay(?:\.[a-z]{2,3})?(?:[:\s].*)?|AliExpress(?:\.com)?(?:[:\s].*)?|LEGO\s+Sets?\b.*|Giochi\s+e\s+giocattol[ai].*|Set\s+da\s+Costruzion[ei].*|Kit\s+da\s+Costruzion[ei].*|Modello\s+da\s+(?:Costruzione|Esposizione).*|Modellismo\s+da\s+Costruir[ea].*|Oggetto\s+da\s+Coll(?:e?z)?ion[ei].*|Astronave\s+Giocattolo.*|Action\s+Figure.*|Ki(?:t)?\.{0,3}$)\s*$/i;
                        const ent = (s) => s
                          .replace(/&#39;/g, "'")
                          .replace(/&quot;/g, '"')
                          .replace(/&amp;/g, "&")
                          .replace(/&nbsp;/g, " ");
                        let s = ent(raw);
                        for (let i = 0; i < 3; i++) {
                          const n = s.replace(SUFFIX_RX, "").replace(/[\s,;:|\-–—]+$/, "");
                          if (n === s) break;
                          s = n;
                        }
                        s = s.replace(/[\s,;:|\-–—]*\b[A-Za-z]{1,3}\.{0,3}\s*$/, "").replace(/\s+/g, " ").trim();
                        if (s.length > 70) {
                          const cut = s.slice(0, 71);
                          const sp = cut.lastIndexOf(" ");
                          s = (sp >= 42 ? cut.slice(0, sp) : cut.slice(0, 70)).replace(/[\s,;:|\-–—]+$/, "") + "…";
                        }
                        setEditing({ ...editing, title_display: s });
                      }}
                      className="text-[10px] font-mono-bdd uppercase tracking-wider px-3 py-1.5 rounded-md bg-[#FBD51C]/15 hover:bg-[#FBD51C]/25 text-[#FBD51C] border border-[#FBD51C]/30"
                    >
                      Pulisci da originale
                    </button>
                  </span>
                }
                hint="Lascia vuoto per usare il titolo grezzo dello store. Riempi per mostrare un titolo curato (consigliato per AdSense)."
              >
                <input
                  data-testid="form-title-display"
                  value={editing.title_display || ""}
                  onChange={(e) => setEditing({ ...editing, title_display: e.target.value })}
                  placeholder="Es. LEGO Star Wars 75347 TIE Bomber"
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-white outline-none focus:border-[#FBD51C]/60"
                />
              </Field>
              <Field label="Titolo originale (marketplace)" hint="Conservato per riferimento interno, non visualizzato sul sito.">
                <input
                  data-testid="form-title"
                  value={editing.title}
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-zinc-400 text-sm outline-none focus:border-[#FBD51C]/60"
                />
              </Field>
              <Field label="URL immagine">
                <input
                  data-testid="form-image"
                  value={editing.image_url}
                  onChange={(e) => setEditing({ ...editing, image_url: e.target.value })}
                  placeholder="https://..."
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-white outline-none focus:border-[#FBD51C]/60"
                />
              </Field>
              <Field label="Link affiliato">
                <div className="flex gap-2">
                  <input
                    data-testid="form-affiliate"
                    value={editing.affiliate_url}
                    onChange={(e) => setEditing({ ...editing, affiliate_url: e.target.value })}
                    onPaste={(e) => {
                      const pasted = (e.clipboardData || window.clipboardData)?.getData("text") || "";
                      const url = pasted.trim();
                      if (!url || !/^https?:\/\//i.test(url)) return;
                      // Only auto-scrape on paste when creating a new deal (no id) and no title yet
                      if (!editing.id && !editing.title) {
                        setTimeout(() => autoFillFromAffiliate(url), 50);
                      }
                    }}
                    placeholder="https://amzn.to/... · https://s.click.aliexpress.com/... · https://ebay.us/..."
                    className="flex-1 bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-white outline-none focus:border-[#FBD51C]/60"
                  />
                  <button
                    type="button"
                    data-testid="form-affiliate-autofill"
                    onClick={() => autoFillFromAffiliate(editing.affiliate_url)}
                    disabled={autoFilling || !editing.affiliate_url}
                    className="shrink-0 inline-flex items-center gap-1.5 bg-[#FBD51C] hover:bg-[#FFE14A] disabled:opacity-40 disabled:cursor-not-allowed text-black font-bold text-xs px-3 py-2.5 rounded-lg"
                    title="Compila titolo, immagine, store e categoria leggendo la pagina del prodotto"
                  >
                    <Download size={12} />
                    {autoFilling ? "Carico…" : "Auto-compila"}
                  </button>
                </div>
                {autoFillMsg && (
                  <p className={`mt-1.5 text-[11px] ${autoFillMsg.ok ? "text-emerald-400" : "text-amber-400"}`}>
                    {autoFillMsg.text}
                  </p>
                )}
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Store">
                  <select
                    data-testid="form-store"
                    value={editing.store}
                    onChange={(e) => setEditing({ ...editing, store: e.target.value })}
                    className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-white outline-none"
                  >
                    {STORES.map((s) => <option key={s} value={s}>{STORE_LABEL[s]}</option>)}
                  </select>
                </Field>
                <Field label="Categoria">
                  <select
                    data-testid="form-category"
                    value={editing.category}
                    onChange={(e) => setEditing({ ...editing, category: e.target.value })}
                    className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-white outline-none"
                  >
                    {officialCats.map((c) => (
                      <option key={c.key} value={c.key}>{c.label}</option>
                    ))}
                  </select>
                </Field>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <Field label="Prezzo (€)">
                  <input
                    data-testid="form-price-current"
                    type="number" step="0.01"
                    value={editing.price_current}
                    onChange={(e) => setEditing({ ...editing, price_current: e.target.value })}
                    className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-white outline-none"
                  />
                </Field>
                <Field label="Prezzo originale (€)">
                  <input
                    data-testid="form-price-original"
                    type="number" step="0.01"
                    value={editing.price_original}
                    onChange={(e) => setEditing({ ...editing, price_original: e.target.value })}
                    className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-white outline-none"
                  />
                </Field>
                <Field label="Sconto %">
                  <input
                    data-testid="form-discount"
                    type="number"
                    value={editing.discount_percent}
                    onChange={(e) => setEditing({ ...editing, discount_percent: e.target.value })}
                    className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-white outline-none"
                  />
                </Field>
              </div>
              <Field label="Descrizione">
                <textarea
                  data-testid="form-description"
                  value={editing.description}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                  rows={4}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-white outline-none focus:border-[#FBD51C]/60"
                />
              </Field>
              <Field
                label={
                  <span className="flex items-center justify-between gap-3">
                    <span>Analisi breve editoriale <span className="text-zinc-500 font-normal">(60-90 parole)</span></span>
                    <button
                      type="button"
                      data-testid="btn-generate-analisi"
                      onClick={generateAnalisi}
                      disabled={generatingAnalisi || !editing.id}
                      title={!editing.id ? "Salva prima l'offerta, poi genera l'analisi" : ""}
                      className="text-[10px] font-mono-bdd uppercase tracking-wider px-3 py-1.5 rounded-md bg-[#FBD51C]/15 hover:bg-[#FBD51C]/25 text-[#FBD51C] border border-[#FBD51C]/30 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {generatingAnalisi ? "Generazione…" : "Genera con AI"}
                    </button>
                  </span>
                }
                hint="Mini-recensione mostrata sotto il prezzo. Aiuta AdSense valutando il sito come 'editoriale'."
              >
                <textarea
                  data-testid="form-analisi"
                  value={editing.analisi_breve || ""}
                  onChange={(e) => setEditing({ ...editing, analisi_breve: e.target.value })}
                  rows={4}
                  placeholder="Esempio: Il set X è una buona occasione per chi cerca…"
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-white outline-none focus:border-[#FBD51C]/60 text-sm"
                />
                {analisiMsg ? (
                  <div className="text-xs mt-1.5 text-zinc-400" data-testid="analisi-msg">{analisiMsg}</div>
                ) : null}
              </Field>
              <Field label="Scadenza offerta (opzionale)" hint="Mostra un countdown live sulla card + dettaglio. Lascia vuoto se l'offerta non ha scadenza.">
                <input
                  data-testid="form-expires"
                  type="datetime-local"
                  value={editing.expires_at}
                  onChange={(e) => setEditing({ ...editing, expires_at: e.target.value })}
                  className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2.5 text-white outline-none focus:border-[#FBD51C]/60"
                />
              </Field>
              <div className="flex items-center gap-6 pt-2">
                <label className="inline-flex items-center gap-2 text-sm text-zinc-300">
                  <input
                    data-testid="form-hot"
                    type="checkbox"
                    checked={!!editing.hot}
                    onChange={(e) => setEditing({ ...editing, hot: e.target.checked })}
                    className="accent-[#FBD51C]"
                  />
                  Hot
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-zinc-300">
                  <input
                    data-testid="form-published"
                    type="checkbox"
                    checked={!!editing.published}
                    onChange={(e) => setEditing({ ...editing, published: e.target.checked })}
                    className="accent-[#FBD51C]"
                  />
                  Pubblicata
                </label>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-zinc-800 flex justify-end gap-3 sticky bottom-0 bg-zinc-900">
              <button onClick={() => setShowForm(false)} className="text-zinc-400 text-sm">Annulla</button>
              <button
                data-testid="form-save"
                onClick={saveDeal}
                className="inline-flex items-center gap-2 bg-[#FBD51C] text-black font-black uppercase tracking-wider text-xs px-4 py-2.5 rounded-lg"
              >
                <Save size={14} /> Salva
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <div className="text-xs font-mono-bdd uppercase tracking-wider text-zinc-400 mb-1">
        {label}
      </div>
      {children}
      {hint ? <div className="text-[11px] text-zinc-500 mt-1">{hint}</div> : null}
    </label>
  );
}

function ProgressBar({ label, state, accent = "emerald", testid, keyField = "current_title" }) {
  const accentMap = {
    emerald: { bar: "from-emerald-400 to-emerald-500", text: "text-emerald-300", bg: "bg-emerald-500/5", border: "border-emerald-500/20" },
    sky:     { bar: "from-sky-400 to-sky-500",         text: "text-sky-300",     bg: "bg-sky-500/5",     border: "border-sky-500/20" },
    purple:  { bar: "from-purple-400 to-purple-500",   text: "text-purple-300",  bg: "bg-purple-500/5",  border: "border-purple-500/20" },
  };
  const a = accentMap[accent] || accentMap.emerald;
  const pct = state.total ? Math.round((state.done / state.total) * 100) : 0;
  return (
    <div className={`mt-3 p-3 rounded-lg ${a.bg} border ${a.border}`} data-testid={testid}>
      <div className="flex items-center justify-between mb-2 text-xs">
        <div className={`font-mono-bdd uppercase tracking-wider ${a.text}`}>
          {state.running ? `${label} · in corso` : `${label} · completato`}
        </div>
        <div className="text-zinc-400">
          {state.done}/{state.total}
          {" · "}<span className="text-emerald-400">{state.ok} ok</span>
          {state.failed > 0 && <><span> · </span><span className="text-rose-400">{state.failed} falliti</span></>}
          {state.skipped ? <><span> · </span><span className="text-amber-400">{state.skipped} saltati</span></> : null}
        </div>
      </div>
      <div className="h-1.5 rounded-full bg-zinc-900 overflow-hidden">
        <div className={`h-full bg-gradient-to-r ${a.bar} transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
      {state.running && state[keyField] ? (
        <div className="mt-2 text-[11px] text-zinc-500 truncate">
          In lavorazione: <span className="text-zinc-300">{state[keyField]}</span>
        </div>
      ) : null}
      {!state.running && state.last_error ? (
        <div className="mt-2 text-[11px] text-rose-400 truncate">Ultimo errore: {state.last_error}</div>
      ) : null}
    </div>
  );
}

function FieldEnd_PLACEHOLDER() { return null; }
function _LegacyField({ label, hint, children }) {
  return (
    <div>
      <div className="text-[10px] font-mono-bdd uppercase tracking-[0.25em] text-zinc-500 mb-2">
        {label}
      </div>
      {children}
      {hint && <div className="text-[11px] text-zinc-600 mt-1">{hint}</div>}
    </div>
  );
}

function SourceBadge({ source }) {
  const s = source || "manual";
  const map = {
    manual: { label: "Manuale", icon: "✍️", cls: "bg-zinc-800 text-zinc-300 border-zinc-700" },
    telegram: { label: "Telegram", icon: "📨", cls: "bg-sky-500/15 text-sky-300 border-sky-500/30" },
    ebay_api: { label: "eBay API", icon: "🏷️", cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  };
  const m = map[s] || map.manual;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold border ${m.cls}`}>
      <span>{m.icon}</span>{m.label}
    </span>
  );
}

function DiagRow({ label, value, good }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-zinc-800 pb-2 last:border-b-0 last:pb-0">
      <div className="text-zinc-500 font-mono-bdd text-[10px] uppercase tracking-wider w-44 shrink-0">
        {label}
      </div>
      <div className={`text-right ${good === false ? "text-rose-400" : good ? "text-emerald-400" : "text-zinc-300"}`}>
        {value}
      </div>
    </div>
  );
}
